import { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, invalidateAdminCache } from '../api.js';
import { parseDate, formatDateDDMMYYYY as formatDate, getTodayMidnight, getCurrentQualification, getQualStatus } from '../utils/date.js';

export default function StudentsPage() {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentDetail, setStudentDetail] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [changePasswordMode, setChangePasswordMode] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [activeQualName, setActiveQualName] = useState(null);
  const [loadedTabs, setLoadedTabs] = useState(new Set());
  const [tabUnitStats, setTabUnitStats] = useState({});
  const [reopeningUnit, setReopeningUnit] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmUnitCode, setConfirmUnitCode] = useState(null);
  const [selectedUnitCodes, setSelectedUnitCodes] = useState([]);
  const [showAddUnitModal, setShowAddUnitModal] = useState(false);
  const [addUnitCode, setAddUnitCode] = useState('');
  const [addUnitStart, setAddUnitStart] = useState('');
  const [addUnitEnd, setAddUnitEnd] = useState('');
  const [banks, setBanks] = useState([]);
  const [banksLoading, setBanksLoading] = useState(false);
  const [gearUnit, setGearUnit] = useState(null);
  const [gearStart, setGearStart] = useState('');
  const [gearEnd, setGearEnd] = useState('');
  const [confirmAction, setConfirmAction] = useState(null); // 'reopen' | 'delete'
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const refreshGen = useRef(0);
  const cooldownRef = useRef(0);
  const intervalRef = useRef(null);

  function getCooldownSeconds() {
    const ts = sessionStorage.getItem('admin_last_refresh');
    if (!ts) return 0;
    const elapsed = Date.now() - parseInt(ts, 10);
    return Math.max(0, Math.ceil((15000 - elapsed) / 1000));
  }

  function startCooldownTimer() {
    if (intervalRef.current) clearInterval(intervalRef.current);
    const tick = () => {
      const remaining = getCooldownSeconds();
      cooldownRef.current = remaining;
      setCooldown(remaining);
      if (remaining <= 0) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    tick();
    intervalRef.current = setInterval(tick, 250);
  }

  function stopCooldownTimer() {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }

  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    const remaining = getCooldownSeconds();
    if (remaining > 0) {
      cooldownRef.current = remaining;
      setCooldown(remaining);
      startCooldownTimer();
    }
    return () => stopCooldownTimer();
  }, []);

  async function handleRefresh() {
    const remaining = getCooldownSeconds();
    if (remaining > 0) return;
    const gen = ++refreshGen.current;
    console.log('[StudentsPage] Refresh clicked (gen ' + gen + ')');
    invalidateAdminCache();
    setRefreshing(true);
    sessionStorage.setItem('admin_last_refresh', String(Date.now()));
    startCooldownTimer();
    const [stuData, subData] = await Promise.all([
      api.getStudents().catch(e => { console.error('[StudentsPage] getStudents error:', e); return { students: [] }; }),
      api.getSubmissions().catch(e => { console.error('[StudentsPage] getSubmissions error:', e); return { submissions: [] }; }),
    ]);
    if (gen !== refreshGen.current) {
      console.log('[StudentsPage] Stale refresh cancelled (gen ' + gen + ')');
      setRefreshing(false);
      return;
    }
    console.log('[StudentsPage] Refresh complete, students:', stuData.students?.length, 'submissions:', subData.submissions?.length);
    setStudents(stuData.students || []);
    setSubmissions(subData.submissions || []);
    setLoading(false);
    setRefreshing(false);
  }

  useEffect(() => {
    let cancelled = false;
    async function run() {
      const [stuData, subData] = await Promise.all([
        api.getStudents().catch(() => ({ students: [] })),
        api.getSubmissions().catch(() => ({ submissions: [] })),
      ]);
      if (cancelled) return;
      setStudents(stuData.students || []);
      setSubmissions(subData.submissions || []);
      setLoading(false);
    }
    run();
    return () => { cancelled = true; };
  }, []);

  // Build submission lookup keyed by optionalID (primary) or username (fallback)
  const submissionMap = {};
  for (const s of submissions) {
    const optId = String(s.optionalID || '').trim();
    if (optId) {
      submissionMap[optId] = s;
    } else {
      submissionMap[String(s.username || '').toLowerCase().trim()] = s;
    }
  }

  // 1. Deduplicate by optionalID (primary) or username (fallback) - BEFORE enrichment
  const seen = new Map();
  for (const s of students) {
    const optId = String(s.optionalID || '').trim();
    const key = optId || String(s.username || '').toLowerCase().trim();
    if (!key) continue;
    // Normalize optionalID to string and keep latest entry per student
    seen.set(key, { ...s, optionalID: optId });
  }
  const uniqueStudents = Array.from(seen.values());

  // Helper to count released units (start date has passed) for a student's qualifications
  function countReleasedUnits(student) {
    const quals = student.qualifications || [];
    let releasedCount = 0;
    const today = getTodayMidnight();
    
    for (const qual of quals) {
      if (qual.units && Array.isArray(qual.units)) {
        for (const unit of qual.units) {
          if (unit.activityStartDate) {
            const startDate = parseDate(unit.activityStartDate);
            if (startDate && startDate <= today) {
              releasedCount++;
            }
          } else {
            // No start date means it's already available
            releasedCount++;
          }
        }
      }
    }
    return releasedCount;
  }

  // 2. Enrich with submission data using optionalID as key
  const enrichedStudents = uniqueStudents.map(s => {
    const optId = String(s.optionalID || '').trim();
    const sub = optId ? submissionMap[optId] : submissionMap[String(s.username || '').toLowerCase().trim()];
    const allAttempts = sub?.attempts || [];
    // Count distinct units attempted (by unitCode in filename)
    const unitsAttempted = new Set(
      allAttempts.map(a => a.unitCode).filter(Boolean)
    ).size;
    return {
      ...s,
      hasSubmission: !!sub && allAttempts.length > 0,
      attempts: allAttempts,
      unitsAttempted,
      releasedUnits: countReleasedUnits(s),
      latestScore: sub?.attempts?.[sub.attempts.length - 1]?.score?.percent,
    };
  });

  // 3. Context-aware search detection
  function getContext(query) {
    const q = query.toLowerCase();
    
    // Digit-only → ID search (exact or partial match within IDs)
    if (/^\d+$/.test(q)) return 'id';
    
    // Check qualification matches (2+ chars) - only if it appears in at least one qualification
    for (const s of enrichedStudents) {
      const qual = String(s.qualificationNames || '').toLowerCase();
      if (qual.includes(q) && q.length >= 2) {
        return 'qualification';
      }
    }
    
    // Default to name search - match first word or anywhere in full name
    return 'name';
  }

  const trimmedSearch = search.trim();
  
  let filtered;
  if (!trimmedSearch) {
    filtered = enrichedStudents;
  } else {
    const q = trimmedSearch.toLowerCase();
    const context = getContext(trimmedSearch);
    
    switch (context) {
      case 'id':
        // Search only in ID field - exact or partial match
        filtered = enrichedStudents.filter(s => {
          const idVal = String(s.optionalID || s.username).toLowerCase();
          return idVal.includes(q);
        });
        break;
      case 'qualification':
        // Search in qualification names - prefix matches first, then substring
        filtered = enrichedStudents.filter(s => 
          String(s.qualificationNames || '').toLowerCase().includes(q)
        );
        break;
      default: // name - match first word or anywhere in full name
        filtered = enrichedStudents.filter(s => {
          const nameLower = String(s.name || '').toLowerCase();
          const words = nameLower.split(/\s+/).filter(Boolean);
          return words.some(w => w.startsWith(q) || w.includes(q)) || nameLower.includes(q);
        });
        break;
    }
  }

  // 4. Sort alphabetically by matched column, prioritizing prefix matches
  filtered.sort((a, b) => {
    if (!trimmedSearch) return 0;
    const context = getContext(trimmedSearch);
    
    let valA, valB;
    switch (context) {
      case 'id':
        valA = String(a.optionalID || a.username || '');
        valB = String(b.optionalID || b.username || '');
        // Numeric sort for IDs
        const numA = parseInt(valA, 10);
        const numB = parseInt(valB, 10);
        if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
        return valA.localeCompare(valB);
      case 'qualification':
        valA = String(a.qualificationNames || '');
        valB = String(b.qualificationNames || '');
        break;
      default: // name - prioritize prefix matches over substring matches
        valA = String(a.name || '');
        valB = String(b.name || '');
        break;
    }
    
    if (context === 'name') {
      const qWords = trimmedSearch.toLowerCase().split(/\s+/);
      
      // Score each student by best prefix match quality
      function getMatchScore(name) {
        const nameLower = name.toLowerCase();
        const words = nameLower.split(/\s+/).filter(Boolean);
        let bestScore = -1;
        
        for (const word of words) {
          if (word.startsWith(trimmedSearch)) {
            // Exact prefix match on a word - score by position (first word is best)
            const firstWordIdx = words.indexOf(word);
            const score = 1000 - firstWordIdx * 10;
            return Math.max(bestScore, score);
          } else if (word.includes(trimmedSearch)) {
            // Substring match within a word
            const idx = words.indexOf(word);
            const score = 500 - idx * 10;
            return Math.max(bestScore, score);
          }
        }
        
        // No single-word match, check full name prefix
        if (nameLower.startsWith(trimmedSearch)) {
          return 200;
        }
        
        // Check each word as a potential start of a multi-word prefix
        for (const word of words) {
          if (word.includes(qWords[0])) {
            const idx = words.indexOf(word);
            return Math.max(bestScore, 100 - idx * 10);
          }
        }
        
        return -1; // no match at all (shouldn't happen since we already filtered)
      }
      
      const scoreA = getMatchScore(valA);
      const scoreB = getMatchScore(valB);
      
      if (scoreA !== scoreB) return scoreB - scoreA;
    } else if (context === 'qualification') {
      // Score qualifications: prefix match first, then substring
      function getQualScore(qual) {
        const qualLower = qual.toLowerCase();
        if (qualLower.startsWith(trimmedSearch)) return 1000;
        if (qualLower.includes(trimmedSearch)) return 500;
        return -1;
      }
      
      const scoreA = getQualScore(valA);
      const scoreB = getQualScore(valB);
      
      if (scoreA !== scoreB) return scoreB - scoreA;
    }
    
    return valA.localeCompare(valB);
  });

  // 5. Pagination - ensure we always have at least 1 page
  const totalPages = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
  const paginatedStudents = filtered.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  async function handleSelectStudent(student) {
    setSelectedStudent(student);
    setShowPassword(false);
    setMessage('');
    setSelectedUnitCodes([]);
    setConfirmAction(null);
    setGearUnit(null);
    
    try {
      const optId = String(student.optionalID || '').trim();
      let detail;
      if (optId) {
        detail = await api.getStudentDetail(optId);
      } else {
        detail = await api.getStudentDetail('');
      }
      setStudentDetail(detail);

      const qualifications = detail.qualifications || [];
      const today = getTodayMidnight();
      const currentQual = getCurrentQualification(qualifications, today) || (qualifications[0] || null);
      
      if (!currentQual) {
        setMessage('No qualifications found for this student.');
        return;
      }

      setActiveQualName(currentQual.name);
      setLoadedTabs(new Set());
      setTabUnitStats({});

      // Lazy-load the current qual's units (first tab)
      loadTabUnits(detail, currentQual);
    } catch (err) {
      console.error('Error loading student detail:', err);
      setMessage('Failed to load student details');
    }
  }

  function loadTabUnits(detail, qual) {
    if (!qual || loadedTabs.has(qual.name)) return; // already cached

    const targetOptionalID = String((detail.optionalID || selectedStudent?.optionalID || '').trim());

    setUnitsLoading(true);

    const sortedUnits = [...(qual.units || [])].sort((a, b) => {
      const dateA = parseDate(a.activityStartDate);
      const dateB = parseDate(b.activityStartDate);
      if (dateA && dateB) return dateA - dateB;
      return 0;
    });

    const unitsPromises = sortedUnits.map(async (unit) => {
      let stats = null;
      try {
        stats = await api.getUnitStatsAdmin(unit.code, qual.name, targetOptionalID);
      } catch {}

      return {
        ...unit,
        qualification: qual.name,
        stats,
      };
    });

    Promise.all(unitsPromises).then(enrichedUnits => {
      setTabUnitStats(prev => ({ ...prev, [qual.name]: enrichedUnits }));
      setLoadedTabs(prev => new Set([...prev, qual.name]));
      setUnitsLoading(false);
    }).catch(() => {
      setUnitsLoading(false);
    });
  }

  function handleQualSwitch(qualName) {
    const qual = (studentDetail?.qualifications || []).find(q => q.name === qualName);
    if (!qual) return;

    setActiveQualName(qualName);
    setSelectedUnitCodes([]);

    if (!loadedTabs.has(qualName)) {
      loadTabUnits(studentDetail, qual);
    }
  }

  function handleBackToList() {
    setSelectedStudent(null);
    setStudentDetail(null);
    setActiveQualName(null);
    setLoadedTabs(new Set());
    setTabUnitStats({});
    setShowPassword(false);
    setUnitsLoading(false);
    setShowConfirmModal(false);
    setConfirmUnitCode(null);
    setMessage('');
  }

  async function handleChangePassword() {
    if (!newPassword || !confirmPassword) {
      setMessage('Please enter both fields.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setMessage('Passwords do not match.');
      return;
    }
    if (newPassword.length < 6) {
      setMessage('Password must be at least 6 characters long.');
      return;
    }
    if (!/^[A-Z0-9]+_[A-Z]{1,5}$/.test(newPassword)) {
      setMessage('Password must be uppercase letters/numbers in format XXXXX_XXX');
      return;
    }

    setChangingPassword(true);
    setMessage('');

    const optId = String(selectedStudent.optionalID || '').trim();
    try {
      const result = await api.changeStudentPassword({ optionalID: optId, newPassword });
      if (result.ok || result.message) {
        setMessage(`Password changed successfully for ${selectedStudent.name}.`);
        setChangePasswordMode(false);
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setMessage(result.error || 'Failed to change password.');
      }
    } catch (err) {
      setMessage(`Error: ${err.message || 'Failed to change password.'}`);
    } finally {
      setChangingPassword(false);
    }
  }

  function handleCancelChangePassword() {
    setChangePasswordMode(false);
    setNewPassword('');
    setConfirmPassword('');
    setMessage('');
  }

  function handleReopenClick(unitCode) {
    setConfirmUnitCode(unitCode);
    setShowConfirmModal(true);
  }

  function handleToggleSelectUnit(code) {
    setSelectedUnitCodes(prev =>
      prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]
    );
  }

  function handleDeleteSelected() {
    setConfirmAction('delete');
    setShowConfirmModal(true);
  }

  async function handleConfirmDelete() {
    setShowConfirmModal(false);
    setConfirmAction(null);
    const qualName = activeQualName;
    const removed = selectedUnitCodes;
    setMessage('');
    try {
      await api.updateStudentUnits({
        optionalID: selectedStudent.optionalID,
        qualificationName: qualName,
        remove: removed,
      });
      setTabUnitStats(prev => ({
        ...prev,
        [qualName]: (prev[qualName] || []).filter(u => !removed.includes(u.code)),
      }));
      setSelectedUnitCodes([]);
      setMessage(`Deleted ${removed.length} unit(s) from ${qualName}`);
    } catch (err) {
      setMessage(`Error: ${err.message}`);
    }
  }

  async function openAddUnitModal() {
    setAddUnitCode('');
    setAddUnitStart('');
    setAddUnitEnd('');
    setShowAddUnitModal(true);
    setBanksLoading(true);
    try {
      const res = await api.listQuestionBanks();
      setBanks(res.banks || []);
    } catch {
      setBanks([]);
    } finally {
      setBanksLoading(false);
    }
  }

  async function handleSaveAddUnit() {
    const code = addUnitCode.trim();
    if (!code) { setMessage('Enter a unit code'); return; }
    setMessage('');
    try {
      const res = await api.updateStudentUnits({
        optionalID: selectedStudent.optionalID,
        qualificationName: activeQualName,
        add: [{ code, activityStartDate: addUnitStart || null, activityEndDate: addUnitEnd || null }],
      });
      const newUnit = (res.qualification.units || []).find(u => u.code.toLowerCase() === code.toLowerCase());
      setTabUnitStats(prev => {
        const existing = prev[activeQualName] || [];
        const idx = existing.findIndex(u => u.code.toLowerCase() === code.toLowerCase());
        const units = idx >= 0
          ? existing.map((u, i) => (i === idx ? newUnit : u))
          : [...existing, newUnit];
        return { ...prev, [activeQualName]: units };
      });
      setShowAddUnitModal(false);
      setMessage(`Added unit ${code} to ${activeQualName}`);
    } catch (err) {
      setMessage(`Error: ${err.message}`);
    }
  }

  function openGearModal(unit) {
    setGearUnit(unit);
    setGearStart(toInputDate(unit.activityStartDate));
    setGearEnd(toInputDate(unit.activityEndDate));
  }

  async function handleSaveGearDates() {
    if (!gearUnit) return;
    const code = gearUnit.code;
    setMessage('');
    try {
      await api.updateUnitDates({
        optionalID: selectedStudent.optionalID,
        qualificationName: activeQualName,
        code,
        activityStartDate: gearStart || null,
        activityEndDate: gearEnd || null,
      });
      setTabUnitStats(prev => ({
        ...prev,
        [activeQualName]: (prev[activeQualName] || []).map(u =>
          u.code === code
            ? { ...u, activityStartDate: gearStart || null, activityEndDate: gearEnd || null }
            : u
        ),
      }));
      setGearUnit(null);
      setMessage(`Updated dates for ${code}`);
    } catch (err) {
      setMessage(`Error: ${err.message}`);
    }
  }

  function handleGearReopen() {
    if (!gearUnit) return;
    setConfirmUnitCode(gearUnit.code);
    setConfirmAction('reopen');
    setGearUnit(null);
    setShowConfirmModal(true);
  }

  async function handleReopenUnit(unitCode) {
    if (!selectedStudent || !studentDetail) return;
    
    const qualifications = studentDetail.qualifications || [];
    let courseName = '';
    for (const qual of qualifications) {
      if (qual.units?.some(u => u.code === unitCode)) {
        courseName = qual.name;
        break;
      }
    }
    
    if (!courseName) return;

    setReopeningUnit(unitCode);
    setMessage('');

    try {
      const result = await api.resetUnitAttempts({
        username: selectedStudent.username,
        optionalID: String(selectedStudent.optionalID || '').trim(),
        course: courseName,
        unitCode,
      });

      if (result.ok) {
        setMessage(`Successfully re-opened ${unitCode}. Student now has 4 fresh attempts.`);

        const optId = String(selectedStudent.optionalID || '').trim();

        // Re-fetch stats for the reset unit and update in-place (tab cache)
        try {
          const updatedStats = await api.getUnitStatsAdmin(unitCode, courseName, optId);
          setTabUnitStats(prev => {
            const units = prev[courseName] || [];
            return { ...prev, [courseName]: units.map(u => 
              u.code === unitCode ? { ...u, stats: updatedStats } : u
            )};
          });
        } catch {}

        // Cache invalidation (dispatched by resetUnitAttempts via clearAdminCache)
        // will trigger re-fetch of all submissions, updating unitsAttempted for this student.
      } else {
        setMessage(`Error: ${result.error || 'Failed to re-open unit'}`);
      }
    } catch (err) {
      setMessage(`Error: ${err.message || 'Failed to re-open unit'}`);
    } finally {
      setReopeningUnit(null);
      setShowConfirmModal(false);
      setConfirmUnitCode(null);
    }
  }

  function handleCloseConfirmModal() {
    setShowConfirmModal(false);
    setConfirmUnitCode(null);
    setConfirmAction(null);
  }

  function handleViewUnitSubmissions(unit) {
    const optId = String(selectedStudent.optionalID || '').trim();
    if (optId) {
      navigate(`/students/${selectedStudent.username}/unit/${encodeURIComponent(unit.code)}?optionalID=${encodeURIComponent(optId)}`);
    } else {
      navigate(`/students/${selectedStudent.username}/unit/${encodeURIComponent(unit.code)}`);
    }
  }

  function toInputDate(value) {
    const d = parseDate(value);
    if (!d) return '';
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  function getOverlappingUnits(unit, otherUnits) {
    const s = parseDate(unit.activityStartDate);
    const e = parseDate(unit.activityEndDate);
    if (!s || !e) return [];
    return otherUnits.filter(u => {
      if (u.code === unit.code) return false;
      const os = parseDate(u.activityStartDate);
      const oe = parseDate(u.activityEndDate);
      if (!os || !oe) return false;
      return s <= oe && os <= e;
    });
  }

  function isUnitBlocked(unit) {
    if (!unit.stats) return false;
    const { attemptsUsed, maxAttempts, blocked } = unit.stats;
    return blocked || (attemptsUsed >= maxAttempts);
  }

  function getUnitStatus(unit) {
    if (!unit.stats) return 'Not started';
    
    const { bestScore, attemptsUsed, maxAttempts, blocked } = unit.stats;
    
    // Check if unit hasn't started yet (no attempts and start date is in the future)
    if (attemptsUsed === 0 && !bestScore && unit.activityStartDate) {
      const startDate = parseDate(unit.activityStartDate);
      if (startDate && startDate > getTodayMidnight()) {
        return `Opens on: ${formatDate(unit.activityStartDate)}`;
      }
    }
    
    if (bestScore !== null && bestScore >= 100) return 'Completed';
    if (blocked || attemptsUsed >= maxAttempts) return 'Max attempts reached';
    if (attemptsUsed > 0) return `${attemptsUsed}/${maxAttempts} attempts`;
    return 'Not started';
  }

 function getUnitStatusColor(unit) {
    const status = getUnitStatus(unit);
    if (status === 'Completed') return '#27ae60';
    if (status.includes('Max')) return '#c0392b';
    if (status.includes('attempts')) return '#e67e22';
    if (status.startsWith('Opens on')) return '#5b6776';
    return '#5b6776';
  }

  function getQualStatusLabel(status) {
    switch (status) {
      case 'active': return 'Active';
      case 'ended': return 'Ended';
      case 'not_started': return 'Not yet started';
      case 'not_stated': return 'Not stated';
      default: return status;
    }
  }

  function getStatusBadgeColor(status) {
    switch (status) {
      case 'active': return '#27ae60';
      case 'ended': return '#5b6776';
      case 'not_started': return '#e67e22';
      default: return '#5b6776';
    }
  }

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#2c3e50' }}>Loading students...</div>;
  }

// Show student detail view when selected
  if (selectedStudent) {
    const qualifications = studentDetail?.qualifications || [];
    const today = getTodayMidnight();
    const allQuals = qualifications.map(q => ({
      ...q,
      status: getQualStatus(q, today),
    }));

    // Aggregate summary across all loaded tabs
    let totalUnits = 0, completedCount = 0, blockedCount = 0, opensSoonCount = 0, notStartedCount = 0;
    for (const qualName of Object.keys(tabUnitStats)) {
      const units = tabUnitStats[qualName] || [];
      totalUnits += units.length;
      completedCount += units.filter(u => getUnitStatus(u) === 'Completed').length;
      blockedCount += units.filter(u => isUnitBlocked(u)).length;
      opensSoonCount += units.filter(u => getUnitStatus(u).startsWith('Opens on')).length;
      notStartedCount += units.filter(u => {
        const s = getUnitStatus(u);
        return !s.includes('attempts') && !s.startsWith('Opens on') && s !== 'Completed' && s !== 'Max attempts reached';
      }).length;
    }

    // On narrow screens, use a select dropdown instead of tabs
    const isNarrow = typeof window !== 'undefined' && window.innerWidth < 560;

    return (
      <div>
        {/* Breadcrumb */}
        <div style={{ marginBottom: 16, fontSize: 13 }}>
          <span 
            style={{ color: 'var(--brand)', textDecoration: 'none', cursor: 'pointer' }}
            onClick={handleBackToList}
          >
            Students
          </span>
          {' > '}<span style={{ color: '#5b6776' }}>{selectedStudent.name || selectedStudent.username}</span>
        </div>

        {/* Header */}
        <h2 style={{ marginTop: 0, marginBottom: 4 }}>{selectedStudent.name || selectedStudent.username}</h2>
        {message && (
          <div style={{ 
            padding: '12px 16px', 
            borderRadius: 4, 
            marginBottom: 16,
            background: message.startsWith('Error') ? '#fdecea' : '#d4edda',
            color: message.startsWith('Error') ? '#c0392b' : '#155d24',
            fontSize: 14,
          }}>
            {message}
          </div>
        )}

        {/* Student Details Card */}
        <div className="card" style={{ padding: 20, marginBottom: 20 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 600 }}>Student Details</h3>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px 24px' }}>
            {[
              ['Optional ID', studentDetail?.optionalID || '\u2014'],
              ['Username', selectedStudent.username],
              ['Name', studentDetail?.name || selectedStudent.name],
              ['Qualification', studentDetail?.qualificationNames || '\u2014'],
            ].map(([label, value]) => (
              <div key={label}>
                <div style={{ fontSize: 12, color: '#5b6776', marginBottom: 4 }}>{label}</div>
                <div style={{ fontWeight: 500 }}>{value}</div>
              </div>
            ))}
          </div>

          {/* Password with blur and change option */}
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid var(--border)` }}>
            <div style={{ fontSize: 12, color: '#5b6776', marginBottom: 4 }}>Password</div>
            {studentDetail?.password ? (
              <>
                {!changePasswordMode ? (
                  <>
                    <div 
                      onClick={() => setShowPassword(!showPassword)}
                      style={{ 
                        cursor: 'pointer',
                        userSelect: showPassword ? 'text' : 'none',
                        padding: '8px 12px',
                        background: '#f8f9fb',
                        borderRadius: 4,
                        fontSize: 14,
                        fontFamily: 'monospace',
                        color: '#2c3e50',
                        position: 'relative',
                      }}
                    >
                      {showPassword ? (
                        studentDetail.password
                      ) : (
                        <span style={{ 
                          filter: 'blur(4px)',
                          userSelect: 'none',
                          pointerEvents: 'none',
                        }}>
                          {studentDetail.password}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => setChangePasswordMode(true)}
                      style={{
                        marginTop: 8,
                        background: '#f0f4f8',
                        color: 'var(--brand)',
                        border: `1px solid var(--border)`,
                        padding: '6px 12px',
                        borderRadius: 4,
                        cursor: 'pointer',
                        fontSize: 13,
                        fontWeight: 500,
                      }}
                    >
                      Change Password
                    </button>
                  </>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <input
                      type="password"
                      placeholder="New password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleChangePassword(); }}
                      style={{ 
                        padding: '8px 12px',
                        border: `1px solid var(--border)`,
                        borderRadius: 4,
                        fontSize: 14,
                        fontFamily: 'monospace',
                      }}
                    />
                    <input
                      type="password"
                      placeholder="Confirm new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleChangePassword(); }}
                      style={{ 
                        padding: '8px 12px',
                        border: `1px solid var(--border)`,
                        borderRadius: 4,
                        fontSize: 14,
                        fontFamily: 'monospace',
                      }}
                    />
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        onClick={handleChangePassword}
                        disabled={changingPassword}
                        style={{
                          background: changingPassword ? '#e67e22' : '#2c6fbb',
                          color: 'white',
                          border: 'none',
                          padding: '8px 16px',
                          borderRadius: 4,
                          cursor: changingPassword ? 'wait' : 'pointer',
                          fontSize: 13,
                          fontWeight: 600,
                        }}
                      >
                        {changingPassword ? 'Changing...' : 'Save Password'}
                      </button>
                      <button
                        onClick={handleCancelChangePassword}
                        style={{
                          background: 'transparent',
                          color: '#5b6776',
                          border: `1px solid var(--border)`,
                          padding: '8px 16px',
                          borderRadius: 4,
                          cursor: 'pointer',
                          fontSize: 13,
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div style={{ padding: '8px 12px', background: '#f8f9fb', borderRadius: 4, fontSize: 14, color: '#5b6776' }}>
                Not available
              </div>
            )}
          </div>
        </div>

        {/* Qualification tabs or select (narrow) */}
        {qualifications.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            {isNarrow ? (
              <select
                value={activeQualName || ''}
                onChange={(e) => handleQualSwitch(e.target.value)}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 4, fontSize: 14 }}
              >
                {allQuals.map(q => (
                  <option key={q.name} value={q.name}>
                    {q.name} ({getQualStatusLabel(q.status)})
                  </option>
                ))}
              </select>
            ) : (
              <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>
                {allQuals.map((q) => {
                  const isActive = q.name === activeQualName;
                  return (
                    <button
                      key={q.name}
                      onClick={() => handleQualSwitch(q.name)}
                      style={{
                        padding: '8px 16px',
                        border: `1px solid ${isActive ? 'var(--brand)' : 'var(--border)'}`,
                        borderRadius: 4,
                        background: isActive ? '#f0f4f8' : 'white',
                        cursor: 'pointer',
                        fontSize: 13,
                        fontWeight: isActive ? 600 : 400,
                        color: isActive ? 'var(--brand)' : '#5b6776',
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                      }}
                    >
                      {q.name}{' '}
                      <span style={{ fontSize: 11, color: getStatusBadgeColor(q.status) }}>
                        ({getQualStatusLabel(q.status)})
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Units for active qualification */}
        {activeQualName && (
          <div className="card" style={{ padding: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '16px 20px 8px' }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>
                Units ({allQuals.find(q => q.name === activeQualName)?.name || ''})
              </h3>
              <div style={{ display: 'flex', gap: 8 }}>
                {selectedUnitCodes.length > 0 && (
                  <button
                    onClick={handleDeleteSelected}
                    style={{ background: '#c0392b', color: 'white', border: 'none', padding: '6px 12px', borderRadius: 4, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
                  >
                    Delete selected ({selectedUnitCodes.length})
                  </button>
                )}
                <button
                  onClick={openAddUnitModal}
                  style={{ background: '#3b82f6', color: 'white', border: 'none', padding: '6px 12px', borderRadius: 4, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
                >
                  + Add unit
                </button>
              </div>
            </div>

            {tabUnitStats[activeQualName] ? (
              (() => {
                const units = tabUnitStats[activeQualName];
                return units.length === 0 ? (
                  <p style={{ padding: '16px 20px', textAlign: 'center', color: '#5b6776' }}>No units found for this qualification.</p>
                ) : (
                  <div>
                    {units.map((unit, idx) => {
                      const blocked = isUnitBlocked(unit);
                      const hasAttempts = unit.stats?.attemptsUsed > 0;

                      return (
                        <div key={unit.code}>
                          <div 
                            style={{ 
                              padding: '16px 20px',
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center',
                              opacity: !unit.stats && (!unit.activityStartDate || parseDate(unit.activityStartDate) > getTodayMidnight()) ? 0.5 : 1,
                              cursor: hasAttempts ? 'pointer' : 'default',
                            }}
                            onClick={() => hasAttempts ? handleViewUnitSubmissions(unit) : null}
                          >
                            <div>
                              <input
                                type="checkbox"
                                checked={selectedUnitCodes.includes(unit.code)}
                                onChange={() => handleToggleSelectUnit(unit.code)}
                                onClick={(e) => e.stopPropagation()}
                                style={{ cursor: 'pointer', marginBottom: 4 }}
                              />
                              <div style={{ fontWeight: 600, fontSize: 14, color: '#2c3e50' }}>{unit.code}</div>
                              {unit.title && (
                                <div style={{ fontSize: 12, color: '#5b6776', marginTop: 2 }}>
                                  {unit.title}
                                </div>
                              )}
                              <div style={{ 
                                fontSize: 13, 
                                fontWeight: 600, 
                                marginTop: 4,
                              }}>
                                <span style={{ color: getUnitStatusColor(unit) }}>{getUnitStatus(unit)}</span>
                              </div>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                              {/* Due date on right */}
                              {unit.activityEndDate && (
                                <span style={{ 
                                  fontSize: 13, 
                                  fontWeight: 500,
                                  color: (() => {
                                    const d = parseDate(unit.activityEndDate);
                                    return d && d < getTodayMidnight() ? '#c0392b' : '#2c3e50';
                                  })(),
                                }}>
                                  Due {formatDate(unit.activityEndDate)}
                                </span>
                              )}

                              {/* Gear: edit dates / re-open */}
                              <button
                                title="Edit dates / Re-open"
                                onClick={(e) => { e.stopPropagation(); openGearModal(unit); }}
                                style={{
                                  background: 'transparent',
                                  color: '#5b6776',
                                  border: '1px solid var(--border)',
                                  padding: '6px 10px',
                                  borderRadius: 4,
                                  cursor: 'pointer',
                                  fontSize: 14,
                                }}
                              >
                                &#9881;
                              </button>
                            </div>
                          </div>
                          {idx < units.length - 1 && (
                            <div style={{ height: 1, background: 'var(--border)', margin: '0 20px' }} />
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()
            ) : unitsLoading ? (
              <p style={{ padding: '16px 20px', textAlign: 'center', color: '#5b6776' }}>Loading units...</p>
            ) : (
              <p style={{ padding: '16px 20px', textAlign: 'center', color: '#5b6776' }}>Select a qualification to view its units.</p>
            )}
          </div>
        )}

        {/* Summary */}
        {totalUnits > 0 && (
          <div style={{ marginTop: 16, fontSize: 13, color: '#5b6776' }}>
            Total units across all qualifications: {totalUnits} | 
{' '}Completed: {completedCount} | 
{' '}Blocked: {blockedCount} | 
{' '}Opens soon: {opensSoonCount} | 
{' '}Not started: {notStartedCount}
          </div>
        )}

        {/* Confirmation modal: re-open unit / delete selected units */}
        {showConfirmModal && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
          }}>
            <div style={{
              background: 'white', borderRadius: 8, padding: 24, maxWidth: 480, width: '90%',
              boxShadow: '0 4px 24px rgba(0,0,0,0.2)'
            }}>
              <h3 style={{ margin: '0 0 16px', color: '#c0392b', fontSize: 18 }}>
                {confirmAction === 'delete' ? '\u{26A0} Confirm Delete Units' : '\u{26A0} Confirm Re-open Assessment'}
              </h3>
              {confirmAction === 'delete' ? (
                <p style={{ margin: '0 0 20px', fontSize: 14, color: '#2c3e50' }}>
                  This removes <strong>{selectedUnitCodes.length}</strong> unit(s) from <strong>{activeQualName}</strong> for{' '}
                  <strong>{selectedStudent?.name || selectedStudent?.username}</strong>.
                  Existing submissions are kept and can be restored later.
                </p>
              ) : (
                <>
                  <p style={{ margin: '0 0 12px', fontSize: 14, color: '#2c3e50' }}>
                    You are about to re-open <strong>{confirmUnitCode}</strong> for{' '}
                    <strong>{selectedStudent?.name || selectedStudent?.username}</strong>.
                  </p>
                  <div style={{
                    background: '#fdf2f2', border: '1px solid #e74c3c', borderRadius: 6,
                    padding: '12px 16px', marginBottom: 20
                  }}>
                    <p style={{ margin: '0 0 8px', fontSize: 14, fontWeight: 600, color: '#c0392b' }}>
                      &#9888; WARNING: IRREVERSIBLE ACTION
                    </p>
                    <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: '#7f1d1d', lineHeight: 1.6 }}>
                      <li>This will permanently delete all active attempt records for this unit</li>
                      <li>Attempts are archived for audit trail but removed from the student's active attempts</li>
                      <li>The student will receive 4 fresh attempts on their next session</li>
                    </ul>
                  </div>
                </>
              )}
              <p style={{ margin: '0 0 20px', fontSize: 13, color: '#5b6776' }}>
                Do you want to proceed?
              </p>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button onClick={handleCloseConfirmModal} style={{
                  background: 'transparent', color: '#5b6776', border: '1px solid #d0d5db',
                  padding: '8px 20px', borderRadius: 4, cursor: 'pointer', fontSize: 14
                }}>Cancel</button>
                <button
                  onClick={() => (confirmAction === 'delete' ? handleConfirmDelete() : handleReopenUnit(confirmUnitCode))}
                  style={{
                    background: '#c0392b', color: 'white', border: 'none',
                    padding: '8px 20px', borderRadius: 4, cursor: 'pointer', fontSize: 14, fontWeight: 600
                  }}
                >
                  {confirmAction === 'delete' ? 'Delete' : 'Confirm Re-open'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add unit modal */}
        {showAddUnitModal && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
          }}>
            <div style={{
              background: 'white', borderRadius: 8, padding: 24, maxWidth: 480, width: '90%',
              boxShadow: '0 4px 24px rgba(0,0,0,0.2)'
            }}>
              <h3 style={{ margin: '0 0 16px', color: '#2c3e50', fontSize: 18 }}>Add unit to {activeQualName}</h3>
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 13, color: '#5b6776', marginBottom: 4 }}>Unit code</label>
                <input
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 4, fontSize: 14 }}
                  value={addUnitCode}
                  onChange={e => setAddUnitCode(e.target.value)}
                  placeholder="e.g. BSBSTR502"
                />
                {banksLoading && <div style={{ fontSize: 12, color: '#5b6776', marginTop: 6 }}>Loading question banks…</div>}
                {!banksLoading && banks.length > 0 && (
                  <div style={{ fontSize: 12, color: '#5b6776', marginTop: 6 }}>
                    Known banks: {banks.join(', ')}
                  </div>
                )}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 13, color: '#5b6776', marginBottom: 4 }}>Start date</label>
                  <input type="date" style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 4 }}
                    value={addUnitStart} onChange={e => setAddUnitStart(e.target.value)} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, color: '#5b6776', marginBottom: 4 }}>End date</label>
                  <input type="date" style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 4 }}
                    value={addUnitEnd} onChange={e => setAddUnitEnd(e.target.value)} />
                </div>
              </div>
              {(() => {
                const overlaps = getOverlappingUnits(
                  { code: addUnitCode, activityStartDate: addUnitStart, activityEndDate: addUnitEnd },
                  tabUnitStats[activeQualName] || []
                );
                return overlaps.length > 0 ? (
                  <div style={{ background: '#fff8e1', border: '1px solid #f0c36d', borderRadius: 4, padding: '8px 12px', fontSize: 13, color: '#8a6d1a', marginBottom: 12 }}>
                    Overlaps with: {overlaps.map(u => u.code).join(', ')}
                  </div>
                ) : null;
              })()}
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button onClick={() => setShowAddUnitModal(false)} style={{
                  background: 'transparent', color: '#5b6776', border: '1px solid #d0d5db',
                  padding: '8px 20px', borderRadius: 4, cursor: 'pointer', fontSize: 14
                }}>Cancel</button>
                <button onClick={handleSaveAddUnit} disabled={!addUnitCode.trim()} style={{
                  background: '#3b82f6', color: 'white', border: 'none',
                  padding: '8px 20px', borderRadius: 4, cursor: 'pointer', fontSize: 14, fontWeight: 600
                }}>Add unit</button>
              </div>
            </div>
          </div>
        )}

        {/* Gear modal: edit dates / re-open */}
        {gearUnit && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
          }}>
            <div style={{
              background: 'white', borderRadius: 8, padding: 24, maxWidth: 440, width: '90%',
              boxShadow: '0 4px 24px rgba(0,0,0,0.2)'
            }}>
              <h3 style={{ margin: '0 0 16px', color: '#2c3e50', fontSize: 18 }}>{gearUnit.code}</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 13, color: '#5b6776', marginBottom: 4 }}>Start date</label>
                  <input type="date" style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 4 }}
                    value={gearStart} onChange={e => setGearStart(e.target.value)} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, color: '#5b6776', marginBottom: 4 }}>End date</label>
                  <input type="date" style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 4 }}
                    value={gearEnd} onChange={e => setGearEnd(e.target.value)} />
                </div>
              </div>
              {(() => {
                const overlaps = getOverlappingUnits(
                  { code: gearUnit.code, activityStartDate: gearStart, activityEndDate: gearEnd },
                  (tabUnitStats[activeQualName] || []).filter(u => u.code !== gearUnit.code)
                );
                return overlaps.length > 0 ? (
                  <div style={{ background: '#fff8e1', border: '1px solid #f0c36d', borderRadius: 4, padding: '8px 12px', fontSize: 13, color: '#8a6d1a', marginBottom: 12 }}>
                    Overlaps with: {overlaps.map(u => u.code).join(', ')}
                  </div>
                ) : null;
              })()}
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button onClick={handleGearReopen} style={{
                  background: '#e67e22', color: 'white', border: 'none',
                  padding: '8px 20px', borderRadius: 4, cursor: 'pointer', fontSize: 14, fontWeight: 600
                }}>Re-open</button>
                <button onClick={() => setGearUnit(null)} style={{
                  background: 'transparent', color: '#5b6776', border: '1px solid #d0d5db',
                  padding: '8px 20px', borderRadius: 4, cursor: 'pointer', fontSize: 14
                }}>Cancel</button>
                <button onClick={handleSaveGearDates} style={{
                  background: '#3b82f6', color: 'white', border: 'none',
                  padding: '8px 20px', borderRadius: 4, cursor: 'pointer', fontSize: 14, fontWeight: 600
                }}>Save dates</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Show student list view
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ marginTop: 0, marginBottom: 0 }}>Students</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button 
            onClick={handleRefresh} 
            disabled={refreshing || cooldown > 0}
            style={{
              background: refreshing ? '#e67e22' : 'transparent',
              color: (refreshing || cooldown > 0) ? '#9aa5b4' : 'var(--brand)',
              border: '1px solid var(--border)',
              padding: '8px 16px',
              borderRadius: 4,
              cursor: (refreshing || cooldown > 0) ? 'not-allowed' : 'pointer',
              fontSize: 13,
              fontWeight: 500,
            }}
          >
            {refreshing ? '\u231B Refreshing...' : cooldown > 0 ? `\u{21BB} Refresh Data (${cooldown}s)` : '\u{21BB} Refresh Data'}
          </button>
          <button onClick={() => navigate('/import-students')} style={{ background: 'var(--brand)', color: 'white', border: 'none', padding: '8px 16px', borderRadius: 4, cursor: 'pointer', fontSize: 13 }}>Import Students (Excel)</button>
        </div>
      </div>

      {/* Search Bar */}
      <div style={{ marginBottom: 16 }}>
        <input
          type="text"
          placeholder="Search by name, ID or qualification..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
          style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
        />
      </div>

      {/* Student Table */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {filtered.length === 0 ? (
          <p style={{ padding: 24, textAlign: 'center', color: '#2c3e50' }}>No students found.</p>
        ) : (
          <>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ borderBottom: `2px solid var(--border)`, textAlign: 'left' }}>
                  <th style={{ padding: '14px 16px', fontWeight: 600 }}>ID</th>
                  <th style={{ padding: '14px 16px', fontWeight: 600 }}>Name</th>
                  <th style={{ padding: '14px 16px', fontWeight: 600 }}>Qualification</th>
                  <th style={{ padding: '14px 16px', fontWeight: 600, textAlign: 'center' }}>Units</th>
                  <th style={{ padding: '14px 16px', fontWeight: 600, textAlign: 'center' }}>Submission</th>
                  <th style={{ padding: '14px 16px', fontWeight: 600, textAlign: 'right' }}>Score</th>
                </tr>
              </thead>
              <tbody>
                {paginatedStudents.map(student => (
                  <StudentRow 
                    key={student.optionalID || student.username} 
                    student={student} 
                    onSelect={() => handleSelectStudent(student)}
                  />
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {filtered.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderTop: `1px solid var(--border)` }}>
                <span style={{ fontSize: 13, color: '#2c3e50' }}>
                  Showing {(currentPage - 1) * rowsPerPage + 1} to {Math.min(currentPage * rowsPerPage, filtered.length)} of {filtered.length} students
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <label htmlFor="rows-per-page" style={{ fontSize: 13, color: '#2c3e50' }}>Rows per page:</label>
                  <select
                    id="rows-per-page"
                    value={rowsPerPage}
                    onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                    style={{ padding: '4px 8px', border: '1px solid var(--border)', borderRadius: 4, fontSize: 13, cursor: 'pointer' }}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={250}>250</option>
                  </select>
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    style={{ padding: '4px 10px', border: '1px solid #b0bec5', borderRadius: 4, background: currentPage === 1 ? '#f5f5f5' : 'white', color: currentPage === 1 ? '#90a4ae' : '#2c3e50', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', fontSize: 13 }}
                  >Previous</button>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages || totalPages === 0}
                    style={{ padding: '4px 10px', border: '1px solid #b0bec5', borderRadius: 4, background: currentPage === totalPages || totalPages === 0 ? '#f5f5f5' : 'white', color: currentPage === totalPages || totalPages === 0 ? '#90a4ae' : '#2c3e50', cursor: currentPage === totalPages || totalPages === 0 ? 'not-allowed' : 'pointer', fontSize: 13 }}
                  >Next</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function StudentRow({ student, onSelect }) {
  return (
    <tr
      onClick={onSelect}
      style={{ cursor: 'pointer', transition: 'background 0.1s' }}
      onMouseEnter={e => e.currentTarget.style.background = '#f8f9fb'}
      onMouseLeave={e => e.currentTarget.style.background = ''}
    >
      <td style={{ padding: '12px 16px', fontWeight: 600 }}>{student.optionalID || student.username}</td>
      <td style={{ padding: '12px 16px' }}>
        <div>{student.name}</div>
        {student.email && <div style={{ fontSize: 12, color: '#3d4a5c', marginTop: 2 }}>{student.email}</div>}
      </td>
      <td style={{ padding: '12px 16px', maxWidth: 200 }}>
        <div title={student.qualificationNames}>{student.qualificationNames || '\u2014'}</div>
      </td>
      <td style={{ padding: '12px 16px', textAlign: 'center' }}>{student.totalUnits || 0}</td>
      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
        {student.hasSubmission ? (
          <span style={{ color: '#155d24', fontWeight: 600, fontSize: 12 }}>{student.unitsAttempted}/{student.releasedUnits || student.totalUnits || 0}</span>
        ) : (
          <span style={{ color: '#922b21', fontWeight: 600, fontSize: 12 }}>Not yet started</span>
        )}
      </td>
      <td style={{ padding: '12px 16px', textAlign: 'right' }}>
        {student.latestScore != null ? (
          student.latestScore === 100
            ? <span style={{ color: '#155d24', fontWeight: 600 }}>Pass</span>
            : <span style={{ color: '#922b21', fontWeight: 600 }}>{student.latestScore}%</span>
        ) : '\u2014'}
      </td>
    </tr>
  );
}
